/**
 * Chart.js configuration utilities for consistent theming
 */

export interface ChartTheme {
  primary: string;
  secondary: string;
  success: string;
  warning: string;
  error: string;
  info: string;
  background: string;
  text: string;
  textSecondary: string;
  border: string;
  grid: string;
}

export const lightTheme: ChartTheme = {
  primary: '#33FF00',
  secondary: '#2BD400',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#3B82F6',
  background: '#FFFFFF',
  text: '#1F2937',
  textSecondary: '#6B7280',
  border: '#E5E7EB',
  grid: '#F3F4F6'
};

export const darkTheme: ChartTheme = {
  primary: '#33FF00',
  secondary: '#2BD400',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#3B82F6',
  background: '#1F2937',
  text: '#F9FAFB',
  textSecondary: '#D1D5DB',
  border: '#374151',
  grid: '#4B5563'
};

export const getChartColors = (theme: ChartTheme, count: number = 5) => {
  const baseColors = [
    theme.primary,
    theme.info,
    theme.success,
    theme.warning,
    theme.error,
    '#8B5CF6', // Purple
    '#F97316', // Orange
    '#06B6D4', // Cyan
    '#84CC16', // Lime
    '#EC4899'  // Pink
  ];
  
  return baseColors.slice(0, count);
};

export const getGradientColors = (theme: ChartTheme, count: number = 5) => {
  const baseColors = getChartColors(theme, count);
  return baseColors.map(color => ({
    primary: color,
    light: `${color}20`,
    dark: `${color}80`
  }));
};

export const getDefaultOptions = (theme: ChartTheme, type: 'line' | 'bar' | 'pie' | 'doughnut' | 'radar' | 'polar') => {
  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: theme.text,
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: theme.background,
        titleColor: theme.text,
        bodyColor: theme.textSecondary,
        borderColor: theme.border,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        titleFont: {
          family: 'Inter, system-ui, sans-serif',
          size: 13,
          weight: 'bold' as const
        },
        bodyFont: {
          family: 'Inter, system-ui, sans-serif',
          size: 12
        }
      }
    },
    scales: type === 'pie' || type === 'doughnut' ? {} : {
      x: {
        grid: {
          color: theme.grid,
          drawBorder: false
        },
        ticks: {
          color: theme.textSecondary,
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 11
          }
        }
      },
      y: {
        grid: {
          color: theme.grid,
          drawBorder: false
        },
        ticks: {
          color: theme.textSecondary,
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 11
          }
        }
      }
    }
  };

  return baseOptions;
};

export const getAnimationConfig = () => ({
  duration: 1000,
  easing: 'easeInOutQuart' as const,
  delay: (context: any) => context.dataIndex * 100
});

export const getHoverConfig = (theme: ChartTheme) => ({
  hoverBackgroundColor: theme.primary + '20',
  hoverBorderColor: theme.primary,
  hoverBorderWidth: 2
});
